import { Link } from "@tanstack/react-router";
import { track } from "../../lib/analytics.ts";
import type { Attention } from "../../lib/overview.ts";
import type { Baby } from "../../lib/queries.ts";
import { useSelectedBaby } from "../../lib/selected-baby.tsx";

/**
 * Entries that may have been forgotten, each with a way straight to that
 * baby's log. An empty list says so plainly — that's the good news.
 */
export function NeedsALook({
  items,
  babies,
  checking,
  limited = false,
}: {
  items: Attention[];
  babies: Baby[];
  /** Two weeks of history still loading: hold off rather than guess. */
  checking: boolean;
  /** The two weeks failed to load, so only the live two days were checked. */
  limited?: boolean;
}) {
  const { setBabyId } = useSelectedBaby();
  const nameOf = (id: string) => babies.find((b) => b.id === id)?.name ?? "";

  return (
    <section className="mt-6" aria-label="Needs a look">
      <div className="flex items-baseline">
        <h2 className="kicker">Needs a look</h2>
        {items.length ? (
          <span className="ml-auto text-[12px] opacity-55">
            {items.length} {items.length === 1 ? "thing" : "things"}
          </span>
        ) : null}
      </div>
      {checking ? (
        <p className="mt-2.5 border-t border-rule py-3 text-[14px] italic opacity-60">
          Checking the last two weeks…
        </p>
      ) : items.length === 0 ? (
        <p className="mt-2.5 border-t border-rule py-3 text-[15px] italic opacity-70">
          {limited
            ? "Nothing looks forgotten in the last two days — the two weeks before couldn't be checked."
            : "Nothing looks forgotten — every log is keeping its usual rhythm."}
        </p>
      ) : (
        <ul className="mt-2.5 grid gap-2.5 md:grid-cols-2">
          {items.map((a) => (
            <li
              key={a.key}
              className="flex flex-col border-l-[3px] border-accent-2 bg-accent-2-tint py-3 pl-4 pr-3 text-accent-2-deep"
            >
              <div className="heading text-[17px]">{a.title}</div>
              <p className="mt-0.5 text-[13px] leading-snug">{a.detail}</p>
              <Link
                to="/"
                onClick={() => {
                  setBabyId(a.babyId);
                  track("needs a look tapped", { check: a.kind });
                }}
                className="-mb-2 -ml-2 mt-auto inline-flex min-h-[44px] items-center self-start rounded-md px-2 text-[14px] underline underline-offset-4 hover:opacity-80"
              >
                Open {nameOf(a.babyId)}'s log →
              </Link>
            </li>
          ))}
        </ul>
      )}
      {limited && items.length ? (
        <p className="mt-2 text-[12px] opacity-60">
          Only the last two days were checked — the two weeks before couldn't load.
        </p>
      ) : null}
    </section>
  );
}
